<template>
  <div style="margin-bottom: 40px; width: 100%;">
    <div class="row">
      <div class="col-md-8">
        <notice
          v-if="matchedClients && !preselectedClient"
          :message="noticeMessage"
          :importance="'primary'"
          :type="'success'"
        />
        <form
          class="tdesk-form contacts-details-form"
          @submit.prevent="handleSubmit()"
        >
          <div class="row">
            <div class="col-xs-6 col-sm-12">
              <div class="form-group required">
                <label for="first_name">First name</label>
                <div
                  v-tooltip:top="existingAccount ? 'An account using this email address was found' : null"
                  tabindex="0"
                  style="position: relative; display: inline-block;"
                  data-toggle="tooltip"
                  class="tdesk-tooltip"
                >
                  <div
                    v-if="existingAccount"
                    style="position:absolute;height: 100%;width:100%; cursor:not-allowed"
                  />
                  <input
                    id="first_name"
                    v-model="participant.first_name"
                    type="text"
                    :disabled="existingAccount"
                    required
                  >
                </div>
              </div>
            </div>
            <div class="col-xs-6 col-sm-12">
              <div class="form-group">
                <label for="last_name">Last name</label>
                <div
                  v-tooltip:top="existingAccount ? 'An account using this email address was found' : null"
                  tabindex="0"
                  style="position: relative; display: inline-block;"
                  data-toggle="tooltip"
                  class="tdesk-tooltip"
                >
                  <div
                    v-if="existingAccount"
                    style="position:absolute;height: 100%;width:100%; cursor:not-allowed"
                  />
                  <input
                    id="last_name"
                    v-model="participant.last_name"
                    type="text"
                    :disabled="existingAccount"
                  >
                </div>
              </div>
            </div>
          </div>
          <div v-if="matchedClients" class="form-group">
            <label for="client_id" style="white-space: normal;">
              Associate booking with company
            </label>
            <select v-if="preselectedClient" id="client_id" disabled>
              <option selected>
                {{ preselectedClient.name }}
              </option>
            </select>
            <select v-else id="client_id" v-model="selectedClientId">
              <option selected value="-1">
                Do not associate
              </option>
              <option v-for="client in matchedClients" :key="client.id" :value="client.id">
                {{ client.name }}
              </option>
            </select>
          </div>
          <div class="form-group required">
            <label for="mobile_phone">Mobile phone number</label>
            <input
              id="mobile_phone"
              v-model="participant.mobile_phone"
              type="text"
              required
            >
          </div>
          <notice
            v-if="participant.phone_number_changed"
            :message="mobileChangedMessage"
            :importance="'primary'"
            :type="'info'"
          />
          <div v-if="order.bookingForSelf">
            <div class="form-group checkbox">
              <input
                id="pre_course_acknowledged"
                v-model="participant.pre_course_acknowledged"
                type="checkbox"
                value="true"
                required
              >
              <label for="pre_course_acknowledged" style="margin-left: 7px;">
                I understand that I will have to complete any required
                pre-course work
              </label>
            </div>
            <div class="form-group checkbox">
              <input
                id="physically_able_acknowledged"
                v-model="participant.physically_able_acknowledged"
                type="checkbox"
                value="true"
                required
              >
              <label
                for="physically_able_acknowledged"
                style="margin-left: 7px;"
              >
                I confirm that I am physically able to complete this course
              </label>
            </div>
          </div>
          <div id="form-errors" class="form-errors">
            <form-errors :errors="formErrors" />
          </div>
          <button class="tdesk-secondary-button pull-right" type="submit">
            <i class="fas fa-caret-right" />
            Next
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
  import Notice from '../../tdesk_notice';
  import FormErrors from "../../form_errors";
  import fireGaEvent from './fire_ga_event';

  export default {
    components: {Notice, FormErrors},
    props: {
      order: {
        required: true,
        type: Object,
      },
      domain: {
        required: true,
        type: Object,
      },
      preselectedClient: {
        required: false,
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        submitted: false,
        error: null,
        formErrors: [],
        matchedClients: this.preselectedClient ? [this.preselectedClient] : null,
        selectedClientId: this.preselectedClient ? this.preselectedClient.id : -1,
        participant: {
          id: 0,
          existing_account: null,
          first_name: null,
          last_name: null,
          mobile_phone: null,
          phone_number_changed: false,
          pre_course_acknowledged: false,
          physically_able_acknowledged: false,
          locked: true,
          email: this.order.contact_email,
        },
      };
    },
    computed: {
      existingAccount() {
        return this.order.contact && this.order.contact.existing_account;
      },
      mobileChangedMessage() {
        return 'Training Desk will also use this updated phone number in your contact details.';
      },
      mobile_phone() {
        return this.participant.mobile_phone;
      },
      noticeMessage() {
        return `You can now select which organisation you are making this booking with. If something seems incorrect, please contact ${this.domain.partner_name} to let us know.`;
      },
      existingParticipant() {
        return this.order.participants.filter(participant => participant.email === this.order.contact_email)[0];
      },
      valid() {
        if (this.order.bookingForSelf) {
          if (!this.participant.pre_course_acknowledged || !this.participant.physically_able_acknowledged) {
            return false;
          }
        }
        return this.participant.first_name && this.participant.mobile_phone && this.participant.email;
      },
    },
    watch: {
      mobile_phone(newValue, oldValue) {
        if (!oldValue) {
          return;
        }
        this.participant.phone_number_changed = true;
      },
    },
    mounted() {
      $('input:enabled').first().focus();
      this.$emit('change-page-title', 'Contact Details');
      this.preFillValues();

      /**
       * When commenced by logged-in client user, the order is associated with the client by default
       * There's no need to recognise client email domain
       */
      if (this.preselectedClient) {
        this.$emit('assign-to-client', {
            id: this.preselectedClient.id,
            name: this.preselectedClient.name,
        });
      } else {
        this.checkEmailForLinkToClient();
      }
    },
    methods: {
      checkEmailDomain() {
        let data = {
          partnership_id: this.domain.partnership_id,
          email: this.participant.email.toLowerCase(),
        };
        axios
            .get(Routes.check_email_domain_for_matching_client_areas_clients_path(data))
            .then(rsp => {
              if (rsp.data) {
                this.matchedClients = [{
                  id: rsp.data.id,
                  name: rsp.data.name,
                }];
                return true;
              }
            }).catch(() => {
              return false;
            });
      },
      checkEmailForLinkToClient() {
        if (this.domain.client_id) {
          return this.$emit('assign-to-client', {
            id: this.domain.client_id,
            name: this.domain.client_name,
          });
        }
        if (this.checkEmailDomain())  {
          return;
        }
        const url = Routes.search_for_linked_clients_by_student_email_partner_students_path({
          partner_id: this.domain.partnership_id,
          email: this.participant.email.toLowerCase(),
        });
        axios
            .get(url)
            .then((response) => {
              if (response.data.user.matched_clients) {
                this.matchedClients = response.data.user.matched_clients;
              }
            });
      },
      preFillValues() {
        if (!this.order.contact) {
          return;
        }
        this.selectedClientId = this.order.client.id;
        this.participant = this.order.contact;
        this.participant.locked = this.order.bookingForSelf;
      },
      handleFormErrors() {
        if (!this.participant.first_name && !this.formErrors.includes('First name is required')) {
          this.formErrors.push('First name is required');
        }
        if (this.order.bookingForSelf) {
          if ((!this.participant.physically_able_acknowledged || !this.participant.pre_course_acknowledged) && !this.formErrors.includes('You must agree to the statements')) {
            this.formErrors.push('You must agree to the statements');
          }
        }
        if (!this.participant.email && !this.formErrors.includes('Email is required')) {
          this.formErrors.push('Email is required');
        }
      },
      handleSubmit() {
        this.handleFormErrors();
        if (this.submitted || !this.valid || this.formErrors.length > 0) {
          return;
        }
        this.submitted = true;
        this.$emit('add-contact', this.participant);

        if (this.order.bookingForSelf) {
          if (this.existingParticipant) {
            this.$emit('modify-participant', this.participant);
          } else {
            this.$emit('add-participant', this.participant);
          }
        }

        if (this.selectedClientId > 0) {
          this.$emit('assign-to-client', {
            id: this.selectedClientId,
            name: this.matchedClients.find(client => client.id === this.selectedClientId).name,
          });
        } else {
          this.$emit('assign-to-client', null);
        }

        fireGaEvent('ssbc_v2_step4_contact_details');
        this.$emit('proceed');
      },
    },
  };
</script>
